import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import cn from '@/utils/cn';

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;

type PopoverContentProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
  // Set false inside a modal Dialog: a portalled popover lands outside the
  // dialog's react-remove-scroll boundary, which blocks scrolling its content
  // (e.g. a long, searchable list). Rendering inline keeps it scrollable.
  portalled?: boolean;
};

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    {
      className,
      align = 'center',
      sideOffset = 4,
      collisionPadding = 8,
      portalled = true,
      ...props
    },
    ref,
  ) => {
    const content = (
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        // Radix avoids collisions by default but with 0 padding, which parks the
        // panel flush against the viewport edge. It also cannot help when the
        // content is wider than the viewport - hence the max-w below.
        collisionPadding={collisionPadding}
        className={cn(
          'z-50 w-72 max-w-[calc(100vw-2rem)] rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none',
          // A popover taller than the space available has no scroll of its own,
          // so its lower half is simply unreachable.
          'max-h-[var(--radix-popover-content-available-height)] overflow-y-auto overscroll-contain',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className,
        )}
        {...props}
      />
    );
    return portalled ? <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal> : content;
  },
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
